import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Radio as RadioIcon, ArrowLeft } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const GENRES = ["Hip-Hop", "R&B", "Pop", "Rock", "Country", "Latin", "Gospel", "EDM", "Jazz", "Reggae", "Afrobeats", "Talk/Variety"];

/**
 * RadioApplyHost — open-to-all form to apply as a Tap IN Radio host.
 *
 * Submission creates a `radio_host_signups` row.  An admin reviews in the
 * Admin → Radio Hosts tab and approves/denies.  On approval the user gets
 * `can_host_radio=true`, which the Radio Host Hub reads to surface the
 * "Create station" flow.
 */
export default function RadioApplyHost() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    show_name: "", display_name: user?.display_name || user?.username || "",
    city: "", bio: "", genres: [], demo_url: "", social_url: "",
  });

  const load = async () => {
    try {
      const { data } = await api.get("/radio/host/signup/mine");
      setStatus(data);
      if (data?.latest) {
        setForm({
          show_name: data.latest.show_name || "",
          display_name: data.latest.display_name || form.display_name,
          city: data.latest.city || "",
          bio: data.latest.bio || "",
          genres: data.latest.genres || [],
          demo_url: data.latest.demo_url || "",
          social_url: data.latest.social_url || "",
        });
      }
    } catch (e) { /* not logged in */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggleGenre = (g) => {
    setForm((f) => {
      const has = f.genres.includes(g);
      return { ...f, genres: has ? f.genres.filter((x) => x !== g) : [...f.genres, g].slice(0, 6) };
    });
  };

  const submit = async () => {
    if (!form.show_name.trim() || !form.display_name.trim()) {
      toast.error("Show name and display name are required"); return;
    }
    setSubmitting(true);
    try {
      await api.post("/radio/host/signup", form);
      toast.success("Application submitted — we'll review shortly");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  if (loading) return <div className="min-h-screen bg-[#05060a] text-white flex items-center justify-center">Loading…</div>;

  const approved = status?.can_host_radio;
  const pending = status?.latest?.status === "pending";
  const denied = status?.latest?.status === "denied";

  return (
    <div data-testid="radio-apply-host" className="min-h-screen bg-[#05060a] text-white">
      <div className="max-w-2xl mx-auto px-6 pt-28 md:pt-32 pb-6">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-white/60 hover:text-white text-sm mb-4">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="flex items-center gap-3 mb-2">
          <RadioIcon className="w-8 h-8 text-tv-accent" />
          <h1 className="font-display font-black text-3xl"><span className="text-red-400">TAP IN</span> RADIO</h1>
        </div>
        <h2 className="text-xl font-bold mb-1">Become a Tap IN Radio Host</h2>
        <p className="text-white/60 text-sm mb-6">Pitch us your show. An admin will review your application — most get a decision within 48 hours.</p>

        {approved && (
          <div data-testid="apply-approved" className="bg-emerald-500/15 border border-emerald-500/40 rounded p-4 mb-4">
            <div className="font-black text-emerald-200">You're cleared to host.</div>
            <div className="text-emerald-100/80 text-sm mt-1">Head to <button onClick={() => navigate("/radio/host")} className="underline font-bold">Radio Host Hub</button> to set up your station and go live.</div>
          </div>
        )}
        {pending && !approved && (
          <div data-testid="apply-pending" className="bg-amber-500/15 border border-amber-500/40 rounded p-4 mb-4">
            <div className="font-black text-amber-200">Your application is under review.</div>
            <div className="text-amber-100/80 text-sm mt-1">You can still update the form below and re-submit until a decision is made.</div>
          </div>
        )}
        {denied && !approved && (
          <div data-testid="apply-denied" className="bg-red-500/15 border border-red-500/40 rounded p-4 mb-4">
            <div className="font-black text-red-200">Your last application was not approved.</div>
            {status.latest?.decision_note && <div className="text-red-100/80 text-sm mt-1">Reviewer note: {status.latest.decision_note}</div>}
            <div className="text-red-100/80 text-sm mt-1">Feel free to refine your pitch and submit again below.</div>
          </div>
        )}

        <div className="bg-white/5 border border-white/10 rounded-lg p-5 space-y-3">
          <label className="block">
            <div className="text-[10px] uppercase tracking-wider font-black text-white/60 mb-1">Show name *</div>
            <input
              data-testid="apply-show-name"
              value={form.show_name}
              onChange={(e) => setForm({ ...form, show_name: e.target.value })}
              placeholder="e.g. Midnight Vibes"
              className="w-full bg-black/40 border border-white/10 rounded px-3 py-2 text-white focus:border-tv-accent outline-none"
            />
          </label>
          <label className="block">
            <div className="text-[10px] uppercase tracking-wider font-black text-white/60 mb-1">Host display name *</div>
            <input
              data-testid="apply-display-name"
              value={form.display_name}
              onChange={(e) => setForm({ ...form, display_name: e.target.value })}
              placeholder="e.g. DJ Tap-In"
              className="w-full bg-black/40 border border-white/10 rounded px-3 py-2 text-white focus:border-tv-accent outline-none"
            />
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="block">
              <div className="text-[10px] uppercase tracking-wider font-black text-white/60 mb-1">City</div>
              <input
                data-testid="apply-city"
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
                placeholder="Atlanta, GA"
                className="w-full bg-black/40 border border-white/10 rounded px-3 py-2 text-white focus:border-tv-accent outline-none"
              />
            </label>
            <label className="block">
              <div className="text-[10px] uppercase tracking-wider font-black text-white/60 mb-1">Social link</div>
              <input
                data-testid="apply-social"
                value={form.social_url}
                onChange={(e) => setForm({ ...form, social_url: e.target.value })}
                placeholder="https://instagram.com/…"
                className="w-full bg-black/40 border border-white/10 rounded px-3 py-2 text-white focus:border-tv-accent outline-none"
              />
            </label>
          </div>
          <label className="block">
            <div className="text-[10px] uppercase tracking-wider font-black text-white/60 mb-1">Genres (up to 6)</div>
            <div className="flex flex-wrap gap-1.5" data-testid="apply-genres">
              {GENRES.map((g) => (
                <button
                  key={g} type="button" onClick={() => toggleGenre(g)}
                  data-testid={`apply-genre-${g.replace(/[^a-z]/gi, "").toLowerCase()}`}
                  className={`text-[11px] font-bold px-2.5 py-1 rounded-full border ${
                    form.genres.includes(g)
                      ? "bg-tv-accent text-black border-tv-accent"
                      : "bg-white/5 text-white/70 border-white/10 hover:bg-white/10"
                  }`}
                >{g}</button>
              ))}
            </div>
          </label>
          <label className="block">
            <div className="text-[10px] uppercase tracking-wider font-black text-white/60 mb-1">Pitch / bio</div>
            <textarea
              data-testid="apply-bio"
              value={form.bio}
              onChange={(e) => setForm({ ...form, bio: e.target.value })}
              placeholder="What's your show about? What makes your mix special? Any broadcast or DJ experience?"
              rows={5}
              className="w-full bg-black/40 border border-white/10 rounded px-3 py-2 text-white focus:border-tv-accent outline-none resize-none"
            />
          </label>
          <label className="block">
            <div className="text-[10px] uppercase tracking-wider font-black text-white/60 mb-1">Demo link (optional)</div>
            <input
              data-testid="apply-demo"
              value={form.demo_url}
              onChange={(e) => setForm({ ...form, demo_url: e.target.value })}
              placeholder="https://soundcloud.com/… or YouTube link"
              className="w-full bg-black/40 border border-white/10 rounded px-3 py-2 text-white focus:border-tv-accent outline-none"
            />
          </label>

          <button
            data-testid="apply-submit"
            onClick={submit}
            disabled={submitting}
            className="w-full bg-tv-accent hover:bg-yellow-400 text-black font-black py-3 rounded disabled:opacity-50"
          >{submitting ? "Submitting…" : (pending ? "Update my application" : "Submit application")}</button>
        </div>
      </div>
    </div>
  );
}
